/**
 * session.ts — who is signed in, for the student app.
 *
 * WHY THIS EXISTS INSTEAD OF READING THE KIDS STORE. Until the repo split the
 * Episodes screens read `useAppStore('user')`, the React bridge over the kids
 * `ReactiveStore`. That store is hydrated by the kids bootstrap and carries XP,
 * lives, gems, clans and worlds — the V1 kids model. Reaching for it from here
 * would mean the student app could not boot without running the whole kids
 * boot sequence first, and `apps/student` importing `apps/legacy` is exactly what
 * the split forbids.
 *
 * So this module asks Supabase directly, for the six fields the four screens
 * actually render. Same client, same session, same `profiles` row the kids app
 * reads — no second source of truth, just a much smaller question.
 *
 * SAME-ORIGIN SESSION. `apps/legacy` is served from `/` and this app from
 * `/app/`; both use the same `storageKey` (see `packages/supabase`), so a
 * student who signed in on the kids side arrives here already authenticated
 * and never sees a second login.
 *
 * `createdAt` is `null` in practice today: the column does not exist on
 * `profiles`. Kept in the shape because Profile already branches on it.
 */
import { useSyncExternalStore } from 'react'
import { waitForClient } from '@theidiomaclub/supabase'
import { asUserId, type UserId } from '@theidiomaclub/kernel'

export type SessionStatus = 'loading' | 'signed-in' | 'signed-out'

export interface StudentUser {
    /** Branded, not a bare string (CLAUDE.md §4.4): `canSeeEpisodes` and the
     *  repos take a `UserId` and must not accept any string that turns up. */
    readonly id: UserId
    /** As the student typed it. Empty string when unknown — never a placeholder name. */
    readonly fullName: string
    /** Consecutive days. 0 is a real answer. */
    readonly streak: number
    /** `null` while `profiles.created_at` does not exist. */
    readonly createdAt: Date | null
    /** Uploaded photo, or empty string — the screens draw initials instead. */
    readonly avatarUrl: string
}

interface SessionState {
    readonly status: SessionStatus
    readonly user: StudentUser | null
}

let state: SessionState = { status: 'loading', user: null }
const listeners = new Set<() => void>()

function set(next: SessionState): void {
    state = next
    listeners.forEach((l) => l())
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => { listeners.delete(listener) }
}

const getSnapshot = (): SessionState => state

/**
 * Resolves the signed-in student exactly once, at boot. Fails closed: any
 * error resolves to `signed-out` rather than leaving the app spinning, and
 * `App.tsx` mounts the door for a signed-out visitor.
 */
export async function loadSession(): Promise<SessionState> {
    try {
        const client = await waitForClient()
        if (!client) { set({ status: 'signed-out', user: null }); return state }

        const { data: auth } = await client.auth.getUser()
        const authUser = auth?.user
        if (!authUser) { set({ status: 'signed-out', user: null }); return state }

        const { data: profile } = await client
            .from('profiles')
            .select('*')
            .eq('id', authUser.id)
            .maybeSingle()

        const row = (profile ?? {}) as Record<string, unknown>
        const rawCreated = row.created_at
        set({
            status: 'signed-in',
            user: {
                id: asUserId(authUser.id),
                fullName: typeof row.full_name === 'string' ? row.full_name : '',
                streak: typeof row.streak_count === 'number' ? row.streak_count : 0,
                createdAt: typeof rawCreated === 'string' ? new Date(rawCreated) : null,
                avatarUrl: typeof row.avatar_url === 'string' ? row.avatar_url : '',
            },
        })
    } catch {
        // Fail loud enough to be visible, closed enough to be safe.
        console.error('[session] could not resolve the signed-in student')
        set({ status: 'signed-out', user: null })
    }
    return state
}

/**
 * The join signs a visitor up without leaving the page, so the boot answer ("signed-out") goes stale the moment they
 * reach Planes, and the app showed the door to a student who had just made an account (measured 2026-10-25). This
 * adopts the session the join just stored — read locally, no round trip, so Planes is not kept waiting — and the
 * full profile follows in the background.
 */
export async function adoptSession(): Promise<SessionState> {
    const client = await waitForClient()
    const stored = client ? (await client.auth.getSession()).data.session?.user : undefined
    if (!stored) {
        set({ status: 'signed-out', user: null })
        return state
    }
    const metaName = (stored.user_metadata as Record<string, unknown> | undefined)?.full_name
    set({
        status: 'signed-in',
        user: {
            id: asUserId(stored.id),
            fullName: typeof metaName === 'string' ? metaName : '',
            streak: 0,
            createdAt: null,
            avatarUrl: '',
        },
    })
    void loadSession()
    return state
}

/** The signed-in student, or `null`. Re-renders when the session resolves. */
export function useStudentUser(): StudentUser | null {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot).user
}

/** `loading` until `loadSession()` has answered. Never guess from `user === null`. */
export function useSessionStatus(): SessionStatus {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot).status
}

/** Test seam. Not used by the app. */
export function __setSessionForTest(next: SessionState): void { set(next) }
